import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import FacebookIcon from '@mui/icons-material/Facebook'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import LanguageIcon from '@mui/icons-material/Language'
import MailOutlineIcon from '@mui/icons-material/EmailOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import XIcon from '@mui/icons-material/X'
import YouTubeIcon from '@mui/icons-material/YouTube'
import React from 'react'
import KarteraThemeSwitch from '../../../template/kartera/KarteraThemeSwitch'
import { BENEFIT_TONES, useQuoteViewerModel } from './quote-viewer-model'
import type { QuoteViewerEmpresa, QuoteViewerSocial } from './quote-viewer-model'

const SOCIAL_ICONS: Record<QuoteViewerSocial, typeof FacebookIcon> = {
    facebook: FacebookIcon,
    youtube: YouTubeIcon,
    tiktok: VideocamOutlinedIcon,
    twitter: XIcon,
    web: LanguageIcon,
}

const BENEFIT_ICONS = [ShieldOutlinedIcon, FavoriteBorderIcon, SavingsOutlinedIcon, HealthAndSafetyOutlinedIcon, VerifiedUserOutlinedIcon, TaskAltIcon]

const NEXT_STEPS = [
    { title: 'Elige tu opción', text: 'Compara y selecciona la que más te convenga.' },
    { title: 'Contacta a tu agente', text: 'Le escribes por WhatsApp con tu opción elegida.' },
    { title: 'Recibe tu póliza', text: 'Tu asesor la tramita con la aseguradora.' },
]

const cardClass = 'rounded-[20px] border border-k-line bg-k-surface'
const h2Class = 'm-0 text-[19px] font-extrabold tracking-[-0.02em] text-k-ink'
const capClass = 'text-xs font-extrabold uppercase tracking-[0.06em] text-k-muted'
const linkClass = 'font-bold text-k-accent-ink no-underline hover:underline'

const Pulse = ({ className }: { className: string }) => <div className={`animate-pulse rounded-[20px] bg-k-line ${className}`} />

type QuoteViewerModel = ReturnType<typeof useQuoteViewerModel>

const Radio = ({ on }: { on: boolean }) => (
    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${on ? 'border-k-primary' : 'border-k-line-strong'}`}>
        <span className={`h-2.5 w-2.5 rounded-full ${on ? 'bg-k-primary' : 'bg-transparent'}`} />
    </span>
)

const OptionsSection = ({ model }: { model: QuoteViewerModel }) => (
    <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
            <h2 className={h2Class}>{model.options.length > 1 ? 'Elige tu opción' : 'Tu propuesta'}</h2>
            {model.frecuencias.length > 1 && (
                <div role="radiogroup" aria-label="Forma de pago" className="inline-flex rounded-xl border border-k-line bg-k-surface p-1 print:hidden">
                    {model.frecuencias.map((forma) => {
                        const on = forma.key === model.frecuencia
                        return (
                            <button
                                key={forma.key}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => model.pickFrecuencia(forma.key)}
                                className={`min-h-[38px] cursor-pointer rounded-[9px] border-0 px-4 text-sm ${on ? 'bg-k-ink font-extrabold text-k-surface' : 'bg-transparent font-semibold text-k-muted hover:text-k-ink'}`}
                            >
                                {forma.label}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
        <div role="radiogroup" aria-label="Opciones de seguro" className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
            {model.options.map((option) => (
                <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={option.on}
                    disabled={!option.price.available}
                    title={option.price.available ? undefined : option.price.nota}
                    onClick={() => model.pickOption(option.id)}
                    className={`relative flex flex-col items-start gap-1 rounded-[18px] p-[18px] text-left text-k-ink ${!option.price.available
                        ? 'cursor-not-allowed border border-dashed border-k-line-strong bg-k-sunken opacity-60'
                        : option.on
                            ? 'cursor-pointer border-2 border-k-primary bg-k-surface shadow-[0_10px_28px_rgba(75,68,216,0.14)]'
                            : 'cursor-pointer border border-k-line bg-k-surface hover:border-[#B9B4F2]'}`}
                >
                    {option.cheapest && (
                        <span className="absolute -top-[11px] left-4 rounded-full bg-gradient-to-r from-[#4B44D8] to-[#8B3FC9] px-2.5 py-[3px] text-[11px] font-extrabold tracking-[0.04em] text-white">
                            MÁS ECONÓMICA
                        </span>
                    )}
                    <span className="flex w-full items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-2">
                            {option.logo && <img src={option.logo} alt="" className="h-6 w-6 shrink-0 rounded-md object-contain" />}
                            <span className="truncate text-[13px] font-bold text-k-muted">{option.cia}</span>
                        </span>
                        {option.price.available
                            ? <Radio on={option.on} />
                            : <span className="shrink-0 rounded-full bg-k-line px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-[0.04em] text-k-muted">No disponible</span>}
                    </span>
                    <span className="text-lg font-extrabold tracking-[-0.02em]">{option.plan}</span>
                    <span className="flex flex-wrap items-baseline gap-x-1">
                        <span className={`font-extrabold tracking-[-0.03em] tabular-nums ${option.price.available ? 'text-[28px]' : 'text-xl text-k-muted'}`}>{option.price.precio}</span>
                        {option.price.per && <span className="whitespace-nowrap text-[13px] font-semibold text-k-muted">{option.price.per}</span>}
                    </span>
                    <span className="text-xs text-k-muted">{option.price.nota}</span>
                    {option.suma && (
                        <>
                            <span className="my-1 h-px w-full bg-k-line-soft" />
                            <span className="text-[13px] text-k-ink-2">{model.sumaLabel}</span>
                            <span className="text-[17px] font-extrabold tabular-nums text-k-ok">{option.suma}</span>
                        </>
                    )}
                </button>
            ))}
        </div>
    </section>
)

const CoverageSection = ({ model }: { model: QuoteViewerModel }) => {
    const { selected } = model
    if (!selected || (model.beneficios.length === 0 && selected.coberturas.length === 0)) return null
    return (
        <section className={`${cardClass} flex flex-col gap-4 px-6 py-[22px]`}>
            <div className="flex flex-col gap-1">
                <span className={capClass}>Lo que incluye {selected.plan}</span>
                <h2 className={h2Class}>Así te protege</h2>
            </div>
            {model.beneficios.length > 0 && (
                <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-3">
                    {model.beneficios.map((item, index) => {
                        const tone = BENEFIT_TONES[index % BENEFIT_TONES.length]
                        const Icon = BENEFIT_ICONS[index % BENEFIT_ICONS.length]
                        return (
                            <div key={`${item.key}-${index}`} className={`flex gap-3 rounded-[14px] p-3.5 ${tone.split(' ')[0]}`}>
                                <span className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-k-surface ${tone.split(' ')[1]}`}>
                                    <Icon style={{ fontSize: 19 }} />
                                </span>
                                <span className="flex min-w-0 flex-col gap-[3px]">
                                    <span className="text-sm font-extrabold text-k-ink">{item.descripcion}</span>
                                    <span className="break-words text-[13px] leading-[1.45] text-k-ink-2">{item.valor}</span>
                                </span>
                            </div>
                        )
                    })}
                </div>
            )}
            {selected.coberturas.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[420px] border-collapse text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-[0.05em] text-k-muted">
                                <th className="py-2 font-bold">Cobertura</th>
                                <th className="py-2 pl-2 text-right font-bold">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selected.coberturas.map((item) => (
                                <tr key={item.key} className="border-t border-k-line-soft">
                                    <td className="py-[11px] font-semibold text-k-ink">{item.descripcion}</td>
                                    <td className="py-[11px] pl-2 text-right tabular-nums text-k-ink-2">{item.valor}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    )
}

const InsuredSection = ({ model }: { model: QuoteViewerModel }) => {
    const { fields } = model
    const correction = model.corregirHref && (
        <span className="ml-auto text-[13px] text-k-muted print:hidden">
            ¿Algún dato no es correcto? <a href={model.corregirHref} target="_blank" rel="noreferrer" className={linkClass}>Avísanos</a>
        </span>
    )

    if (fields.asegurados && model.asegurados.length > 0) {
        return (
            <section className={`${cardClass} flex flex-wrap items-center gap-x-5 gap-y-3 px-6 py-[18px]`}>
                <span className={`${capClass} basis-full`}>Asegurados</span>
                {model.asegurados.map((item) => (
                    <span key={item.key} className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-k-accent-soft text-[13px] font-extrabold text-k-accent-ink">{item.iniciales}</span>
                        <span className="flex flex-col">
                            <span className="text-[15px] font-bold text-k-ink">{item.nombre}</span>
                            {item.detalle && <span className="text-[13px] text-k-muted">{item.detalle}</span>}
                        </span>
                    </span>
                ))}
                {correction}
            </section>
        )
    }

    if (fields.vehiculo && model.vehiculos.length > 0) {
        return (
            <section className={`${cardClass} flex flex-wrap items-center gap-x-5 gap-y-3 px-6 py-[18px]`}>
                <span className={`${capClass} basis-full`}>Vehículo asegurado</span>
                {model.vehiculos.map((item) => (
                    <span key={item.key} className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tone-blue text-tone-blue-ink"><DirectionsCarOutlinedIcon style={{ fontSize: 18 }} /></span>
                        <span className="flex flex-col">
                            <span className="text-[15px] font-bold text-k-ink">{item.descripcion}</span>
                            {item.modelo && <span className="text-[13px] text-k-muted">Modelo {item.modelo}</span>}
                        </span>
                    </span>
                ))}
                {correction}
            </section>
        )
    }

    if (fields.bien && model.bien) {
        return (
            <section className={`${cardClass} flex flex-col gap-3 px-6 py-[18px]`}>
                <span className={capClass}>Bien asegurado</span>
                <div className="flex items-start gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tone-orange text-tone-orange-ink"><HomeOutlinedIcon style={{ fontSize: 18 }} /></span>
                    <p className="m-0 whitespace-pre-line break-words text-sm leading-relaxed text-k-ink-2">{model.bien}</p>
                </div>
                {correction}
            </section>
        )
    }

    return null
}

const ChoiceCard = ({ model }: { model: QuoteViewerModel }) => {
    const { selected, selectedPrice } = model
    if (!selected || !selectedPrice) return null
    return (
        <section className={`${cardClass} flex flex-col gap-3.5 p-5 shadow-[0_12px_32px_rgba(27,29,41,0.06)]`}>
            <span className={capClass}>Tu elección</span>
            <div className="flex flex-col gap-0.5">
                <span className="text-sm font-bold text-k-muted">{selected.cia}</span>
                <span className="text-xl font-extrabold tracking-[-0.02em] text-k-ink">{selected.plan}</span>
            </div>
            <div className="flex flex-col gap-0.5 rounded-[14px] bg-k-accent-soft px-4 py-3.5">
                <span className="flex flex-wrap items-baseline gap-1">
                    <span className="text-[32px] font-extrabold tracking-[-0.03em] tabular-nums text-k-ink">{selectedPrice.precio}</span>
                    {selectedPrice.per && <span className="text-sm font-bold text-k-accent-ink">{selectedPrice.per}</span>}
                </span>
                <span className="text-[13px] text-k-ink-2">{selectedPrice.nota}</span>
            </div>
            <dl className="m-0 flex flex-col gap-2 text-sm">
                {selected.suma && (
                    <div className="flex justify-between gap-2"><dt className="text-k-muted">{model.sumaLabel}</dt><dd className="m-0 font-extrabold tabular-nums text-k-ok">{selected.suma}</dd></div>
                )}
                {model.frecuenciaLabel && (
                    <div className="flex justify-between gap-2"><dt className="text-k-muted">Forma de pago</dt><dd className="m-0 font-bold text-k-ink">{model.frecuenciaLabel}</dd></div>
                )}
                <div className="flex justify-between gap-2"><dt className="text-k-muted">Moneda</dt><dd className="m-0 font-bold text-k-ink">{selected.moneda}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-k-muted">Inicio de cobertura</dt><dd className="m-0 font-bold text-k-ink">Al emitir la póliza</dd></div>
            </dl>
            <div className="flex flex-col gap-3.5 print:hidden">
                {model.vigencia.expired && (
                    <p role="alert" className="m-0 rounded-xl bg-k-danger-soft px-3 py-2.5 text-[13px] font-semibold leading-snug text-k-danger-ink">
                        Esta cotización ya venció. Pide a tu asesor que la actualice.
                    </p>
                )}
                {model.contactoHref && (
                    <a
                        href={model.contactoHref}
                        target="_blank"
                        rel="noreferrer"
                        className="flex h-[54px] items-center justify-center gap-2 rounded-[14px] bg-k-primary text-base font-extrabold text-white no-underline shadow-[0_8px_20px_rgba(75,68,216,0.3)] hover:brightness-105"
                    >
                        {model.contactoWhatsapp ? <WhatsAppIcon style={{ fontSize: 20 }} /> : <MailOutlineIcon style={{ fontSize: 20 }} />}
                        Contactar a mi agente
                    </a>
                )}
                <button type="button" onClick={model.descargar} className="min-h-9 cursor-pointer border-0 bg-transparent text-[13px] font-bold text-k-accent-ink hover:underline">
                    Descargar propuesta en PDF
                </button>
            </div>
        </section>
    )
}

const AdvisorCard = ({ empresa }: { empresa: QuoteViewerEmpresa }) => (
    <section className={`${cardClass} flex flex-col gap-3 px-5 py-[18px]`}>
        <span className={capClass}>Tu asesor</span>
        <div className="flex items-center gap-3">
            {empresa.logoUrl
                ? <img src={empresa.logoUrl} alt="" className="h-[46px] w-[46px] shrink-0 rounded-full border border-k-line bg-k-bg object-contain" />
                : <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#3E6BE6] via-[#8B3FC9] to-[#EC3B6C] text-[15px] font-extrabold text-white">{empresa.iniciales}</span>}
            <span className="flex min-w-0 flex-col">
                <span className="text-[15px] font-extrabold text-k-ink">{empresa.nombre || 'Tu agente de seguros'}</span>
                <span className="text-[13px] text-k-muted">Agente de seguros</span>
            </span>
        </div>
        <div className="flex flex-col gap-2 text-sm">
            {empresa.telefonos.map((tel) => (
                tel.href
                    ? <a key={tel.label} href={tel.href} className="flex items-center gap-2.5 text-k-ink no-underline hover:underline"><CallOutlinedIcon className="text-k-muted" style={{ fontSize: 16 }} />{tel.label}</a>
                    : <span key={tel.label} className="flex items-center gap-2.5 text-k-ink"><CallOutlinedIcon className="text-k-muted" style={{ fontSize: 16 }} />{tel.label}</span>
            ))}
            {empresa.correos.map((correo) => (
                <a key={correo} href={`mailto:${correo}`} className="flex items-center gap-2.5 break-all text-k-ink no-underline hover:underline">
                    <MailOutlineIcon className="text-k-muted" style={{ fontSize: 16 }} />{correo}
                </a>
            ))}
            {empresa.direccion && (
                <span className="flex items-start gap-2.5 text-k-muted"><PlaceOutlinedIcon style={{ fontSize: 16, marginTop: 2 }} />{empresa.direccion}</span>
            )}
        </div>
        {empresa.redes.length > 0 && (
            <div className="flex flex-wrap gap-2 print:hidden">
                {empresa.redes.map((red) => {
                    const Icon = SOCIAL_ICONS[red.key]
                    return (
                        <a
                            key={red.key}
                            href={red.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={red.label}
                            title={red.label}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line-strong text-k-ink no-underline hover:bg-k-hover"
                        >
                            <Icon style={{ fontSize: 16 }} />
                        </a>
                    )
                })}
            </div>
        )}
    </section>
)

const QuoteViewer = () => {
    const model = useQuoteViewerModel()
    const { cotizacion, empresa } = model

    return (
        <div className="k-root min-h-dvh bg-k-bg font-kartera text-k-ink">
            <header className="border-b border-k-line bg-k-surface">
                <div className="mx-auto flex w-full max-w-[1080px] flex-wrap items-center justify-between gap-2.5 px-5 py-3.5">
                    <div className="flex min-w-0 items-center gap-3">
                        {empresa?.logoUrl
                            ? <img src={empresa.logoUrl} alt={empresa.nombre || 'Logotipo'} className="h-10 max-w-[160px] rounded-[11px] object-contain" />
                            : <img src="/brand/kartera365-icon.png" alt="" className="h-10 w-10 rounded-[11px]" />}
                        <span className="flex min-w-0 flex-col leading-tight">
                            <span className="truncate text-base font-extrabold text-k-ink">{empresa?.nombre || 'Kartera 365'}</span>
                            <span className="text-xs text-k-muted">Agente de seguros</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                        {model.vigencia.label && (
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-[7px] text-[13px] font-bold ${model.vigencia.expired ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-tone-orange text-tone-orange-ink'}`}>
                                <AccessTimeIcon style={{ fontSize: 15 }} />{model.vigencia.label}
                            </span>
                        )}
                        <span className="print:hidden"><KarteraThemeSwitch compact /></span>
                    </div>
                </div>
            </header>

            {model.loading && (
                <main className="mx-auto flex w-full max-w-[1080px] flex-wrap items-start gap-5 px-5 pb-10 pt-6" aria-busy="true" aria-label="Cargando cotización">
                    <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-[18px]">
                        <Pulse className="h-40 w-full" />
                        <Pulse className="h-64 w-full" />
                        <Pulse className="h-48 w-full" />
                    </div>
                    <Pulse className="h-96 flex-[1_1_300px]" />
                </main>
            )}

            {model.error && !model.loading && (
                <main className="mx-auto w-full max-w-[720px] px-5 py-8">
                    <div className="flex flex-col items-center gap-3 rounded-[20px] border border-k-danger/30 bg-k-danger-soft px-6 py-12 text-center">
                        <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 36 }} />
                        <p className="m-0 text-base font-bold text-k-danger-ink">{model.error}</p>
                    </div>
                </main>
            )}

            {cotizacion && !model.loading && (
                <main className="mx-auto flex w-full max-w-[1080px] flex-wrap items-start gap-5 px-5 pb-10 pt-6">
                    <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-[18px]">
                        <section className={`${cardClass} flex flex-col gap-3.5 px-[26px] pb-[22px] pt-[26px]`}>
                            <span className="text-sm font-semibold text-k-muted">Cotización n.º {model.numero} · Seguro de {model.ramo}</span>
                            <h1 className="m-0 text-[clamp(26px,4vw,36px)] font-extrabold leading-[1.12] tracking-[-0.035em] text-k-ink">{model.title}</h1>
                            <p className="m-0 max-w-[600px] text-base leading-[1.55] text-k-ink-2">{model.intro}</p>
                        </section>

                        {model.options.length > 0 && <OptionsSection model={model} />}

                        <CoverageSection model={model} />

                        <section className={`${cardClass} flex flex-col gap-3.5 px-6 py-[22px] print:hidden`}>
                            <h2 className={h2Class}>¿Qué sigue?</h2>
                            <ol className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 p-0">
                                {NEXT_STEPS.map((paso, index) => (
                                    <li key={paso.title} className="flex items-start gap-3">
                                        <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-k-accent-soft text-sm font-extrabold text-k-accent-ink">{index + 1}</span>
                                        <span className="flex flex-col gap-0.5">
                                            <span className="text-sm font-extrabold text-k-ink">{paso.title}</span>
                                            <span className="text-[13px] leading-[1.45] text-k-muted">{paso.text}</span>
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </section>

                        <InsuredSection model={model} />
                    </div>

                    <aside aria-label="Tu elección" className="flex max-w-full flex-[1_1_300px] flex-col gap-3.5 md:sticky md:top-4">
                        <ChoiceCard model={model} />
                        {empresa && <AdvisorCard empresa={empresa} />}
                    </aside>
                </main>
            )}

            <footer className="mx-auto flex w-full max-w-[1080px] flex-col gap-2 px-5 py-5 text-xs leading-normal text-k-muted-2">
                <p className="m-0">Cotización informativa sujeta a la aceptación y suscripción de la aseguradora. Los montos pueden variar si cambian los datos declarados.</p>
                <div className="flex flex-wrap justify-between gap-2">
                    <span className="flex items-center gap-1.5">
                        <img src="/brand/kartera365-icon.png" alt="" className="h-4 w-4 rounded" />
                        Cotización generada con Kartera 365 · © {model.year}
                    </span>
                    {empresa && empresa.redes.length > 0 && (
                        <span className="flex flex-wrap gap-3.5 print:hidden">
                            {empresa.redes.map((red) => (
                                <a key={red.key} href={red.href} target="_blank" rel="noreferrer" className="text-k-muted-2 no-underline hover:underline">{red.label}</a>
                            ))}
                        </span>
                    )}
                </div>
            </footer>
        </div>
    )
}

export default QuoteViewer
